/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ProgramBaseProps {
  id: string;
  startTime: Date | null;
  endTime: Date | null;
  baseId: string;
  programId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ProgramBaseRecord extends Record<ProgramBaseProps>({
  id: null as any,
  startTime: null as any,
  endTime: null as any,
  baseId: null as any,
  programId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    startTime: string | number | Date | null;
    endTime: string | number | Date | null;
    baseId: string;
    programId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ProgramBaseRecord {
    if (json == null) {
      throw new Error('ProgramBaseRecord.fromJS: parameter cannot be null');
    }
    return new ProgramBaseRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ProgramBaseJson.id',
      ),
      startTime: json.startTime != null ? new Date(json.startTime) : null,
      endTime: json.endTime != null ? new Date(json.endTime) : null,
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'ProgramBaseJson.baseId',
      ),
      programId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'programId', '00000000-0000-0000-0000-000000000000'),
        'ProgramBaseJson.programId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ProgramBaseJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    startTime: string | number | Date | null;
    endTime: string | number | Date | null;
    baseId: string;
    programId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ProgramBaseRecord, Error[]] {
    if (json == null) {
      throw new Error('ProgramBaseRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ProgramBaseRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ProgramBaseJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTime: tryOrDefault(
        () => (json.startTime != null ? new Date(json.startTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTime: tryOrDefault(
        () => (json.endTime != null ? new Date(json.endTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'ProgramBaseJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      programId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'programId', '00000000-0000-0000-0000-000000000000'),
            'ProgramBaseJson.programId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ProgramBaseJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ProgramBaseProps) {
    super(props);
    if (props == null) {
      throw new Error('ProgramBaseRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ProgramBaseViewModelBase implements ProgramBaseProps {
  protected readonly _props: ProgramBaseProps;
  private _json?: ProgramBaseProps;

  get id(): string {
    return this._props.id;
  }
  get startTime(): Date | null {
    return this._props.startTime;
  }
  get endTime(): Date | null {
    return this._props.endTime;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get programId(): string {
    return this._props.programId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ProgramBaseProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        startTime: this._props.startTime,
        endTime: this._props.endTime,
        baseId: this._props.baseId,
        programId: this._props.programId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllProgramBaseEntities = (state: AppState) =>
  state.entityStores.programBase.entities;

export const selectProgramBaseById = (id: string) => (state: AppState) =>
  state.entityStores.programBase.entities.get(id);

export const selectProgramBaseMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.programBase.meta.get(id);

export const selectProgramBaseAndMetadataById = (id: string) =>
  createSelector(
    selectProgramBaseById(id),
    selectProgramBaseMetadataById(id),
    (programBase, meta) => ({ programBase, meta }),
  );

export function selectProgramBaseEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllProgramBaseEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ProgramBaseRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectProgramBaseEntitiesByStartTime(startTime: Date) {
  return createSelector(selectAllProgramBaseEntities, (entities) =>
    entities.filter((e) => e.startTime === startTime).valueSeq(),
  );
}

export function selectProgramBaseEntitiesByEndTime(endTime: Date) {
  return createSelector(selectAllProgramBaseEntities, (entities) =>
    entities.filter((e) => e.endTime === endTime).valueSeq(),
  );
}

export function selectProgramBaseEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllProgramBaseEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectProgramBaseEntitiesByProgramId(programId: string) {
  return createSelector(selectAllProgramBaseEntities, (entities) =>
    entities.filter((e) => e.programId === programId).valueSeq(),
  );
}

export function selectProgramBaseEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllProgramBaseEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
